'use client'

import { useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import useSWR from 'swr'
import { Crown } from 'lucide-react'
import type { LoginResult, SwitchableIdentitiesResult } from '@feltos/shared'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { fetcher, request } from '@/lib/request'
import { ÅDialog } from './dialog'
import { ÅSelect } from './select'

interface ÅDevLauncherProps {
  currentStaffId?: string
}

function DevLauncher({ currentStaffId }: ÅDevLauncherProps) {
  const router = useRouter()
  /** 电视大屏没有员工会话，浮标既盖画面又会一直 401 */
  const isHidden = usePathname().startsWith('/display')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedStaffId, setSelectedStaffId] = useState(currentStaffId ?? '')
  const [error, setError] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const { data } = useSWR<SwitchableIdentitiesResult>(
    isHidden ? null : '/api/auth/identities',
    fetcher,
  )

  const items =
    data?.identities.map((identity) => ({
      value: identity.staffId,
      label: identity.label,
    })) ?? []

  if (isHidden) return null

  function openDialog() {
    setSelectedStaffId(currentStaffId ?? '')
    setError(null)
    setIsDialogOpen(true)
  }

  async function confirm() {
    if (!selectedStaffId) return
    setIsBusy(true)
    setError(null)
    try {
      const result = await request<LoginResult>('/api/auth/switch-identity', {
        method: 'POST',
        json: { staffId: selectedStaffId },
        fallback: '切换身份失败',
      })
      setIsDialogOpen(false)
      router.replace(result.redirect)
      router.refresh()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '切换身份失败')
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="dev-launcher" aria-label="开发捷径">
          <Crown className="size-5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" sideOffset={8}>
          <DropdownMenuItem onClick={openDialog}>切换身份</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ÅDialog
        open={isDialogOpen}
        title="切换身份"
        description="选择租户与员工身份后进入对应端"
        isBusy={isBusy}
        actions={[
          {
            id: 'confirm',
            label: '确定',
            loadingLabel: '切换中…',
            isDisabled: !selectedStaffId,
            onClick: confirm,
          },
        ]}
        onOpenChange={setIsDialogOpen}
      >
        <div className="flex flex-col gap-4">
          <ÅSelect
            items={items}
            value={selectedStaffId}
            onValueChange={(value) => setSelectedStaffId(value ?? '')}
            aria-label="选择身份"
            placeholder="选择身份"
            emptyLabel="没有匹配的身份"
            className="w-full"
          />
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
      </ÅDialog>
    </>
  )
}

export { DevLauncher as ÅDevLauncher }
